import React, { useState, useMemo } from "react";
import { useDispatch } from "react-redux";
import { Modal } from "antd";
import { setBrokerPurposeAction } from "../../redux/actions/brokerAction";
import { useToast } from "components/common/custom-toast/Toast";
import { AlertTriangle, Monitor, Bot } from "lucide-react";
import { MemoryOutlined, TouchAppOutlined } from "@mui/icons-material";

const BrokerPurposeModal = ({ brokers, show, onComplete }) => {
  const dispatch = useDispatch();
  const toast = useToast();
  const [selections, setSelections] = useState({});
  const [understood, setUnderstood] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const unpurposedBrokers = useMemo(() => {
    return (brokers || []).filter(b => b.type !== 'papertrading' && (!b.purpose || b.purpose === ""));
  }, [brokers]);

  const handleSelect = (brokerId, purpose) => {
    setSelections(prev => ({ ...prev, [brokerId]: purpose }));
  };

  const allSelected = unpurposedBrokers.length > 0 &&
    unpurposedBrokers.every(b => selections[b.id]);

  const handleSubmit = async () => {
    if (!allSelected || !understood) return;
    setSubmitting(true);

    try {
      const results = await Promise.all(
        unpurposedBrokers.map(b =>
          dispatch(setBrokerPurposeAction(b.id, selections[b.id]))
        )
      );

      const allSuccess = results.every(r => r?.status === true);
      if (allSuccess) {
        toast.success("Broker purpose set successfully for all accounts");
        onComplete?.();
      } else {
        const failedCount = results.filter(r => !r?.status).length;
        toast.error(`Failed to set purpose for ${failedCount} broker(s). Please try again.`);
      }
    } catch (error) {
      console.error("Error setting broker purposes:", error);
      toast.error("An error occurred while setting broker purposes.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!show || unpurposedBrokers.length === 0) return null;

  return (
    <Modal
      open={show}
      closable={false}
      maskClosable={false}
      keyboard={false}
      footer={null}
      centered
      width={520}
      className="broker-purpose-modal"
    >
      <div className="broker-purpose-modal-content">
        <div className="broker-purpose-modal-header">
          <AlertTriangle size={22} className="broker-purpose-modal-icon" />
          <h5 className="broker-purpose-modal-title">Setup Required</h5>
        </div>
        <p className="broker-purpose-modal-subtitle">
          Choose a purpose for each broker account. This choice is permanent and cannot be changed later.
        </p>

        <div className="broker-purpose-modal-list">
          {unpurposedBrokers.map(broker => (
            <div key={broker.id} className="broker-purpose-modal-item">
              <div className="broker-purpose-modal-item-header">
                <span className="broker-purpose-modal-broker-name">
                  {broker.name || broker.userName || broker.id}
                </span>
                <span className="broker-purpose-modal-broker-type">
                  {broker.type}
                </span>
              </div>
              <div className="broker-purpose-radio-group">
                <label
                  className={`broker-purpose-radio-option ${selections[broker.id] === "terminal" ? "selected" : ""}`}
                >
                  <input
                    type="radio"
                    name={`purpose-${broker.id}`}
                    value="terminal"
                    checked={selections[broker.id] === "terminal"}
                    onChange={() => handleSelect(broker.id, "terminal")}
                  />
                  <TouchAppOutlined size={16} className="broker-purpose-radio-icon" />
                  <span className="broker-purpose-radio-label">Terminal</span>
                  <span className="broker-purpose-radio-desc">Manual trading</span>
                </label>
                <label
                  className={`broker-purpose-radio-option ${selections[broker.id] === "algo" ? "selected" : ""}`}
                >
                  <input
                    type="radio"
                    name={`purpose-${broker.id}`}
                    value="algo"
                    checked={selections[broker.id] === "algo"}
                    onChange={() => handleSelect(broker.id, "algo")}
                  />
                  <MemoryOutlined size={16} className="broker-purpose-radio-icon" />
                  <span className="broker-purpose-radio-label">Algo</span>
                  <span className="broker-purpose-radio-desc">Run strategies</span>
                </label>
              </div>
              {selections[broker.id] === "terminal" && (
                <div className="broker-purpose-info-box">
                  <p className="broker-purpose-info-strong">This account will be used for <strong>Terminal</strong>.</p>
                  <p className="broker-purpose-info-subtitle">When you confirm:</p>
                  <ul className="broker-purpose-info-list">
                    <li>Any strategies currently linked to this broker will be unlinked.</li>
                    <li>All existing positions on this account will be re-synced fresh from your broker (as terminal positions).</li>
                    <li>Your strategies can no longer run on this account. It can be used with other brokers which are selected for algo purpose.</li>
                  </ul>
                </div>
              )}
              {selections[broker.id] === "algo" && (
                <div className="broker-purpose-info-box">
                  <p className="broker-purpose-info-strong">This account will be used for <strong>Algo Signals only</strong>.</p>
                  <p className="broker-purpose-info-subtitle">When you confirm:</p>
                  <ul className="broker-purpose-info-list">
                    <li>All manual positions, orders, and trades on this account will be cleared from QuikTrade.</li>
                    <li>Strategies currently running on this account will continue running unchanged.</li>
                    <li>You won't be able to place manual trades on this account from QuikTrade Terminal anymore.</li>
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="broker-purpose-modal-warning">
          <AlertTriangle size={14} className="flex-shrink-0" style={{marginTop: '2px'}} />
          <span>
            You can't undo this choice without deleting the broker and linking it fresh as new broker.
          </span>
        </div>

        <label className="broker-purpose-modal-checkbox">
          <input
            type="checkbox"
            checked={understood}
            onChange={(e) => setUnderstood(e.target.checked)}
          />
          <span>I understand this action is permanent and may affect existing data</span>
        </label>

        <button
          className="broker-purpose-modal-submit"
          disabled={!allSelected || !understood || submitting}
          onClick={handleSubmit}
        >
          {submitting ? "Setting up..." : "Confirm & Initialize"}
        </button>
      </div>
    </Modal>
  );
};

export default BrokerPurposeModal;